import { useState, type ReactNode } from "react";
import {
  BirdIcon,
  DatabaseIcon,
  FileTextIcon,
  FilmIcon,
  ImageIcon,
  LayersIcon,
  MenuIcon,
  MessageSquareIcon,
  NotebookIcon,
  PuzzleIcon,
  SettingsIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react";
import type { InstallRecord } from "@woodpecker/protocol";
import { useStore } from "../store/store";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/overlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../lib/cn";
import { LANGS, useLang, useT, type Lang } from "../i18n";
import { WalletBlock } from "./WalletBlock";

const NAV = [
  { path: "conversations", label: "shell.menu.conversations", icon: MessageSquareIcon },
  { path: "services", label: "shell.menu.services", icon: LayersIcon },
  { path: "extensions", label: "shell.menu.extensions", icon: PuzzleIcon },
] as const;
const SETTINGS = { path: "settings", label: "shell.menu.settings", icon: SettingsIcon } as const;

/** The icons a manifest may name (D39, D77); anything else is the puzzle piece. */
const ICONS: Record<string, LucideIcon> = {
  "file-text": FileTextIcon,
  database: DatabaseIcon,
  zap: ZapIcon,
  notebook: NotebookIcon,
  image: ImageIcon,
  film: FilmIcon,
  puzzle: PuzzleIcon,
};

/** The installed and enabled extensions in this browser's order (D69): the ordered ids first, the rest as installed. */
export function orderedExtensions(installed: InstallRecord[], order: string[]): InstallRecord[] {
  const byId = new Map(installed.map((x) => [x.id, x]));
  const first = order.flatMap((id) => byId.get(id) ?? []);
  const rest = installed.filter((x) => !order.includes(x.id));
  return [...first, ...rest].filter((x) => x.enabled);
}

function Brand() {
  const t = useT();
  return (
    <div className="flex items-center gap-2 font-semibold">
      <span className="flex size-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <BirdIcon className="size-4" />
      </span>
      <span>{t("shell.brand")}</span>
    </div>
  );
}

const ITEM =
  "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";

function Nav({ active, activeSub, onPick }: { active: string; activeSub?: string; onPick?: () => void }) {
  const t = useT();
  const { installed, menuOrder, signedIn } = useStore();
  const items = signedIn ? orderedExtensions(installed, menuOrder) : [];
  const link = (item: { path: string; label: string; icon: LucideIcon }) => (
    <a
      key={item.path}
      href={`#/${item.path}`}
      onClick={onPick}
      className={cn(ITEM, active === item.path && !activeSub && "bg-muted text-foreground")}
    >
      <item.icon className="size-4" />
      {t(item.label)}
    </a>
  );
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(link)}
      {items.map((x) => {
        const Icon = ICONS[x.icon ?? ""] ?? PuzzleIcon;
        return (
          <a
            key={x.id}
            href={`#/extensions/${encodeURIComponent(x.id)}`}
            onClick={onPick}
            title={x.id}
            className={cn(ITEM, active === "extensions" && activeSub === x.id && "bg-muted text-foreground")}
          >
            <Icon className="size-4" />
            <span className="truncate">{x.name}</span>
          </a>
        );
      })}
      {link(SETTINGS)}
    </nav>
  );
}

function LanguageSwitch() {
  const { lang, setLang } = useLang();
  const t = useT();
  return (
    <Select value={lang} onValueChange={(v) => setLang(v as Lang)}>
      <SelectTrigger size="sm" aria-label={t("shell.language")} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LANGS.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            {l.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Shell({ active, activeSub, children }: { active: string; activeSub?: string; children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-svh">
      <aside className="sticky top-0 hidden h-svh w-60 shrink-0 flex-col border-r bg-sidebar md:flex">
        <div className="flex h-14 shrink-0 items-center border-b px-4">
          <Brand />
        </div>
        <div className="p-3">
          <Nav active={active} activeSub={activeSub} />
        </div>
        <div className="mt-auto flex flex-col gap-2 p-3">
          <LanguageSwitch />
          <WalletBlock />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/90 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen(true)}
            aria-label={t("shell.openMenu")}
          >
            <MenuIcon />
          </Button>
        </header>
        <main className="mx-auto w-full max-w-6xl min-w-0 flex-1 p-4 md:p-6">{children}</main>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 p-4">
          <SheetHeader className="p-0">
            <SheetTitle>
              <Brand />
            </SheetTitle>
          </SheetHeader>
          <Nav active={active} activeSub={activeSub} onPick={() => setOpen(false)} />
          <div className="mt-auto flex flex-col gap-2">
            <LanguageSwitch />
            <WalletBlock onPick={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function useHashRoute(): string[] {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffectOnce(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  });
  return hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
}

export function navigate(path: string): void {
  window.location.hash = path;
}

import { useEffect } from "react";
function useEffectOnce(fn: () => void | (() => void)) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fn, []);
}
